/**
 * Cockpit visual editor — the AI AUTHOR panel.
 *
 * A prompt box that calls `POST /argus-live/dashboards/:id/author` (via
 * `useAuthorCockpit`). On success the route has authored + PERSISTED a new
 * CockpitSpec version; we hand `result.spec` up so the editor loads it for
 * review/edit before publish. On failure we surface the route's structured
 * re-author errors HONESTLY (the per-node validation issues) plus an option to
 * load the last best-effort candidate into the editor to finish by hand.
 *
 * An optional archetype dropdown seeds the author from one of the canonical
 * scaffolds (the ~80% path). The available binding/action ids are passed so the
 * authored spec can only reference declared refs.
 */

import { useState } from 'react';
import { Sparkles, Loader2, AlertTriangle, FileWarning } from 'lucide-react';
import { COCKPIT_ARCHETYPE_SLUGS, type CockpitArchetypeSlug } from '@argusai/shared';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useAuthorCockpit,
  type AuthorCockpitError,
  type AuthorCockpitResult,
} from '@/lib/use-argus-live';
import { prettyLabel } from './cockpit-editor-model';

const NO_ARCHETYPE = '__none__';

export interface CockpitAiAuthorPanelProps {
  dashboardId: string;
  bindingIds: string[];
  actionIds: string[];
  disabled?: boolean;
  /** Called with the authored spec (201) for review/edit before publish. */
  onAuthored: (result: AuthorCockpitResult) => void;
  /**
   * Called when the user chooses to load the last best-effort candidate after a
   * handoff failure, so they can finish it by hand.
   */
  onLoadCandidate: (candidate: unknown) => void;
}

export function CockpitAiAuthorPanel({
  dashboardId,
  bindingIds,
  actionIds,
  disabled,
  onAuthored,
  onLoadCandidate,
}: CockpitAiAuthorPanelProps) {
  const [prompt, setPrompt] = useState('');
  const [archetype, setArchetype] = useState<string>(NO_ARCHETYPE);
  const author = useAuthorCockpit();
  const lastError = author.error as AuthorCockpitError | null;

  const run = () => {
    if (!prompt.trim() || disabled) return;
    author.mutate(
      {
        dashboardId,
        prompt: prompt.trim(),
        archetype:
          archetype === NO_ARCHETYPE
            ? undefined
            : (archetype as CockpitArchetypeSlug),
        availableBindingIds: bindingIds,
        availableActionIds: actionIds,
      },
      {
        onSuccess: (result) => onAuthored(result),
      },
    );
  };

  return (
    <div className="flex flex-col gap-3 p-3">
      <div>
        <Label htmlFor="cockpit-ai-prompt" className="text-xs font-medium">
          Describe the cockpit
        </Label>
        <Textarea
          id="cockpit-ai-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={disabled || author.isPending}
          placeholder="e.g. A pump health monitor: vibration + bearing-temp metric cards, a 24h vibration line chart, and a status grid of all pumps."
          rows={4}
          maxLength={8000}
          className="mt-1 text-xs"
          data-testid="cockpit-ai-prompt"
        />
        <p className="mt-1 text-[10px] text-muted-foreground">
          Authored by Argus internal models from the closed component library.
        </p>
      </div>

      <div>
        <Label className="text-xs font-medium">Start from an archetype (optional)</Label>
        <Select value={archetype} onValueChange={setArchetype} disabled={disabled || author.isPending}>
          <SelectTrigger className="mt-1 h-8 text-xs" aria-label="Archetype">
            <SelectValue placeholder="No scaffold" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_ARCHETYPE} className="text-xs italic text-muted-foreground">
              No scaffold — author from scratch
            </SelectItem>
            {COCKPIT_ARCHETYPE_SLUGS.map((slug) => (
              <SelectItem key={slug} value={slug} className="text-xs">
                {prettyLabel(slug)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        type="button"
        onClick={run}
        disabled={disabled || author.isPending || !prompt.trim()}
        data-testid="cockpit-ai-author-run"
      >
        {author.isPending ? (
          <>
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            Authoring…
          </>
        ) : (
          <>
            <Sparkles className="mr-1.5 h-4 w-4" />
            Author cockpit
          </>
        )}
      </Button>

      {author.isPending ? (
        <p className="text-[11px] text-muted-foreground">
          Composing from the catalog — this runs up to 3 internal-model attempts and
          may take a moment.
        </p>
      ) : null}

      {/* Honest failure surface — the re-author loop's structured errors. */}
      {lastError ? (
        <div
          className="rounded-md border border-amber-500/40 bg-amber-50 p-2.5 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
          data-testid="cockpit-ai-author-error"
        >
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span className="text-xs font-semibold">
              {lastError.code === 'AUTHORING_UNAVAILABLE'
                ? 'The authoring model is unavailable'
                : 'Could not produce a valid cockpit'}
            </span>
          </div>
          <p className="mt-1 text-[11px]">
            {lastError.message}
            {lastError.attempts ? ` (after ${lastError.attempts} attempts)` : ''}
          </p>
          {lastError.errors && lastError.errors.length > 0 ? (
            <ul className="mt-1.5 max-h-32 list-disc overflow-y-auto pl-4 text-[10px]">
              {lastError.errors.slice(0, 12).map((e, i) => (
                <li key={i}>
                  <span className="font-mono">{e.code}</span>
                  {e.path && e.path !== '(root)' ? <span className="font-mono"> @ {e.path}</span> : null}
                  {': '}
                  {e.message}
                </li>
              ))}
            </ul>
          ) : null}
          {lastError.lastCandidate !== undefined ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2 h-7 text-[11px]"
              onClick={() => onLoadCandidate(lastError.lastCandidate)}
              data-testid="cockpit-ai-load-candidate"
            >
              <FileWarning className="mr-1 h-3 w-3" />
              Load best-effort draft into the editor
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
